/**
 * Schémas du référentiel des personnes — Écran 2, recherche universelle.
 *
 * Importés par l'API (qui valide et sérialise) et par le front (qui valide
 * la réponse). Ce que l'API ne met pas ici ne sort pas : un écran non
 * médical ne reçoit rien de médical (RG-PA-07), et c'est le schéma de
 * réponse qui le garantit, pas la discipline du développeur.
 */
import { z } from 'zod';
import { MOTIFS_NON_ELIGIBLE, PARCOURS_POSSIBLES, STATUTS_ADHERENT } from '../rules/eligibilite.js';
import { ORDRES_DE_TRI } from './administration.js';
import { champNoteChargeeALEnrolement, schemaNoteChargee } from './note-chargee.js';
import { schemaPriseEnCharge } from './prise-en-charge.js';
import { TYPES_BENEFICIAIRE } from '../rules/beneficiaire.js';
import { CONDITIONS_AYANT_DROIT_SANS_CONFIRMATION } from '../rules/ayant-droit-sans-confirmation.js';
import { MOTIFS_DEPART_SALARIE } from '../rules/depart-salarie.js';
import { dateCivileAMadagascar } from '../rules/date-civile.js';
import { LIENS_AYANT_DROIT, MOTIFS_REFUS_SUR_PIECE, PRECISION_PIECE_MAX, MOTIFS_RETRAIT_AYANT_DROIT, } from '../rules/ayants-droit.js';
// Reprises valeur pour valeur des enums Prisma. Un test côté API compare les
// deux listes : un écart se voit à la CI, pas sur un compte que la base
// refuse d'écrire.
export const STATUTS_PERSONNE = ['ACTIF', 'INACTIF'];
export const QUALITES_IDENTITE = ['FORTE', 'MOYENNE', 'FAIBLE'];
export const ETATS_CONFIRMATION = ['CONFIRME', 'A_CONFIRMER', 'INFIRME'];
export const SEXES = ['M', 'F'];
export const TYPES_JUSTIFICATIF = [
    'ACTE_MARIAGE',
    'ACTE_NAISSANCE',
    'LIVRET_FAMILLE',
    'AUTRE',
];
export const ORIGINES_DONNEE = [
    'IMPORT_DNS',
    'SAISIE_MANUELLE',
    'RECONSTITUE_AFFLUENCES',
    'ENROLEMENT_GUICHET',
];
/**
 * Un seul champ de recherche : nom, prénom, matricule ou entreprise
 * (CCT_01 § 3.2, Écran 2). C'est ce qui tient la cible
 * « patient trouvé et éligibilité vérifiée en moins de 3 clics » (§ 4.2).
 *
 * Deux caractères au minimum : en dessous, la réponse est un annuaire, pas
 * une recherche — et le référentiel des personnes n'a pas à se feuilleter.
 */
/**
 * Les colonnes de l'accueil, triées **par le serveur** : la liste est
 * paginée, et trier la page affichée mentirait dès le vingt-sixième
 * patient. L'entreprise et l'éligibilité d'un ayant droit sont celles de
 * son titulaire, et le tri le sait.
 *
 * `eligibilite` n'est accepté que de qui porte `eligibilite:check` : trier
 * par verdict, c'est le lire.
 */
export const TRIS_PERSONNES = ['nom', 'matricule', 'entreprise', 'lien', 'eligibilite'];
export const schemaRecherchePersonnes = z.object({
    recherche: z.string().trim().min(2, 'Deux caractères au minimum.').max(120),
    page: z.coerce.number().int().min(1).default(1),
    limite: z.coerce.number().int().min(1).max(100).default(25),
    tri: z.enum(TRIS_PERSONNES).default('nom'),
    ordre: z.enum(ORDRES_DE_TRI).default('asc'),
});
/** Forme sérialisable de `ResultatEligibilite` (rules/eligibilite.ts). */
export const schemaResultatEligibilite = z.discriminatedUnion('eligible', [
    z.object({ eligible: z.literal(true) }),
    z.object({
        eligible: z.literal(false),
        motif: z.enum(MOTIFS_NON_ELIGIBLE),
        /** Affichable tel quel à l'agent d'accueil — le front ne le réécrit pas. */
        message: z.string(),
    }),
]);
const schemaOffreResume = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
    /** Le rang de l'offre : la plus haute est la meilleure ; `null` : pas encore placée. */
    rang: z.number().int().nullable(),
});
/**
 * Un écart d'offre signalé et pas encore tranché (R5, R7) : la personne
 * attend au comptoir, l'entrée en file est refusée tant qu'il est ouvert.
 * Pour un ayant droit, celui de son titulaire compte aussi (R6).
 */
export const schemaSignalementOffreEnCours = z.object({
    id: z.uuid(),
    /** La personne signalée : un ayant droit attend aussi pour son titulaire. */
    personneId: z.uuid(),
    ouvertLe: z.iso.datetime(),
    site: z.object({ id: z.uuid(), nom: z.string() }),
    /** `null` : « Autre », l'offre lue n'est pas au catalogue (la note le dit). */
    offreLue: z.object({ code: z.string(), nom: z.string() }).nullable(),
});
const schemaAdherentResume = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
    statut: z.enum(STATUTS_ADHERENT),
    /** CP-07 — AFS, FUNTOA, FUNABE… : l'accueil voit pour qui il reçoit. */
    entiteAffiliation: z.object({ code: z.string(), nom: z.string() }),
});
/**
 * Une personne telle que l'accueil la voit.
 *
 * Données administratives uniquement — pas de `siteId` (le référentiel est
 * central, CCT § 3.3), pas de champ médical. L'éligibilité est **calculée
 * par le serveur** et jointe au résultat : l'agent n'a pas d'écran
 * intermédiaire à ouvrir pour la connaître.
 */
export const schemaPersonneTrouvee = z.object({
    id: z.uuid(),
    typeBeneficiaire: z.enum(TYPES_BENEFICIAIRE),
    nom: z.string(),
    prenom: z.string().nullable(),
    /** Tel quel, zéros de tête compris — jamais un nombre (CP-10). */
    matricule: z.string().nullable(),
    /** `AAAA-MM-JJ` sans heure : c'est une date civile. */
    dateNaissance: z.iso.date().nullable(),
    sexe: z.enum(SEXES).nullable(),
    statut: z.enum(STATUTS_PERSONNE),
    qualiteIdentite: z.enum(QUALITES_IDENTITE),
    /** `A_CONFIRMER` : enrôlé au guichet, servi, pas encore confirmé au DNS. */
    etatConfirmation: z.enum(ETATS_CONFIRMATION),
    /** L'entreprise, via le contrat du titulaire. `null` pour un client externe. */
    adherent: schemaAdherentResume.nullable(),
    /** Pour un ayant droit : le salarié auquel il se rattache. */
    titulaire: z
        .object({
        id: z.uuid(),
        nom: z.string(),
        prenom: z.string().nullable(),
        matricule: z.string().nullable(),
        /** Décision 489 — l'ayant droit affiche le statut de l'agent dont il dépend. */
        statut: z.enum(STATUTS_PERSONNE).optional(),
        /** S1438 — et « à confirmer » tant que la chargée n'a pas confirmé l'agent. */
        etatConfirmation: z.enum(ETATS_CONFIRMATION).optional(),
    })
        .nullable(),
    /**
     * Le niveau d'offre : SMIE, SMIE+, CLASSIC, SILVER, GOLD, PLATINIUM —
     * toujours celle du contrat, celui du salarié pour un ayant droit
     * (décision 386). Les dix premières prestations sont couvertes partout ;
     * l'hospitalisation, la lunetterie et l'accouchement varient d'une offre
     * à l'autre. L'agent d'accueil doit le voir SANS ouvrir un second écran :
     * le registre papier porte déjà une colonne PRODUIT, et une consigne y
     * demande de vérifier le niveau avant de servir.
     */
    offre: schemaOffreResume.nullable(),
    /**
     * Décisions 341 et 409 — les prestations que l'offre ouvre à un patient
     * couvert (SMIE+ RETRAITE : le généraliste seul). `null` : l'offre ouvre
     * tout. L'écran d'entrée ne propose que celles-ci ; le serveur refuse le reste.
     */
    prestationsOuvertes: z.array(z.string()).nullable().optional(),
    /** Écart d'offre ouvert (R7). Toujours `null` quand la fonction est éteinte. */
    signalementOffre: schemaSignalementOffreEnCours.nullable(),
    /**
     * `null` quand le rôle appelant lit le référentiel sans porter
     * `eligibilite:check` — la matrice CCT_01 § 2.1 sépare l'identification
     * de la vérification (PA-01 / PA-02).
     */
    eligibilite: schemaResultatEligibilite.nullable(),
    parcours: z.enum(PARCOURS_POSSIBLES).nullable(),
});
export const schemaPagePersonnes = z.object({
    elements: z.array(schemaPersonneTrouvee),
    page: z.number().int(),
    limite: z.number().int(),
    total: z.number().int(),
    /**
     * Décision 348 — les mots tapés qui désignent l'entreprise (« afs » dans
     * « tolotra afs ») : « Créer la fiche » ne les met pas dans le nom.
     */
    motsEntreprise: z.array(z.string()).default([]),
});
// ─────────────────────────────────────────────────────────────────────
//  Avant de créer — réunion du 15/10, la famille (phase 1)
// ─────────────────────────────────────────────────────────────────────
/** Ce que l'écran de création sait au moment de demander. */
export const schemaRequeteFichesExistantes = z.object({
    nom: z.string().trim().min(1).max(120),
    prenom: z.string().trim().max(120).optional(),
    dateNaissance: z.iso.date(),
});
/**
 * Une fiche qui désigne probablement la même personne (`memePersonneProbable`),
 * où qu'elle soit rattachée : sous un autre salarié, ou salariée elle-même —
 * le conjoint qui travaille dans une entreprise adhérente.
 */
export const schemaFicheExistante = z.object({
    id: z.uuid(),
    typeBeneficiaire: z.enum(TYPES_BENEFICIAIRE),
    nom: z.string(),
    prenom: z.string().nullable(),
    dateNaissance: z.iso.date().nullable(),
    statut: z.enum(STATUTS_PERSONNE),
    etatConfirmation: z.enum(ETATS_CONFIRMATION),
    /** Son salarié, pour un conjoint ou un enfant. */
    titulaire: z.object({ id: z.uuid(), nom: z.string(), prenom: z.string().nullable() }).nullable(),
    /** L'entreprise du salarié, ou la sienne. */
    entreprise: z.string().nullable(),
});
export const schemaFichesExistantes = z.object({ elements: z.array(schemaFicheExistante) });
// ─────────────────────────────────────────────────────────────────────
//  Fiche patient — Écran 3, maquette « Résultat d'éligibilité »
// ─────────────────────────────────────────────────────────────────────
export const schemaParametresFiche = z.object({ id: z.uuid() });
/**
 * Les deux conditions de RG-PA-01, **affichées séparément** : l'agent voit
 * sur laquelle la décision repose, et peut l'expliquer au patient. Le
 * verdict, lui, reste `eligibilite` — ces deux booléens l'éclairent, ils ne
 * le remplacent pas.
 */
export const schemaConditionsEligibilite = z.object({
    /** RG-PA-01a — sur la liste active issue du dernier DNS (RG-PA-04). */
    presentAuDernierDns: z.boolean(),
    /** RG-PA-01b — l'entreprise n'est ni suspendue ni résiliée. */
    entrepriseCouvre: z.boolean(),
    /** Instant de réception du dernier DNS de l'entreprise ; `null` s'il n'en est jamais arrivé. */
    dernierDnsRecuLe: z.iso.datetime().nullable(),
    /**
     * `true` pour un ayant droit : ce sont les conditions de son titulaire,
     * dont il hérite (RG-PA-03) — un DNS ne déclare jamais les ayants droit
     * par leur nom (CP-01).
     */
    heritees: z.boolean(),
    /**
     * La fiche ELLE-MÊME est confirmée par la chargée de clientèle — jamais
     * héritée : un ayant droit enrôlé au guichet attend sa propre confirmation.
     */
    confirmee: z.boolean(),
    /**
     * Décision 409 (M1) — la chargée a infirmé le salarié (la fiche elle-même,
     * ou son titulaire pour un ayant droit) : ce n'est pas un DNS qui l'a sorti.
     */
    salarieInfirme: z.boolean(),
});
/**
 * La fiche administrative d'une personne, et les ayants droit qui s'y
 * rattachent.
 *
 * Toujours rien de médical (RG-PA-07) : c'est la fiche de l'accueil, pas le
 * dossier du médecin. Ce qui s'ajoute à la ligne de recherche, ce sont les
 * traits que l'import apporte et que la chargée de clientèle complète —
 * fonction, affectation, entrée, origine — et l'écart entre les ayants droit
 * **déclarés** au DNS et ceux **enrôlés**.
 */
/**
 * Photo d'identité (CCT_01 § Écran 3 — « identité complète : photo, genre,
 * date de naissance, employeur »).
 *
 * L'image ne voyage jamais dans le corps d'une fiche : elle se demande à
 * part, par une route authentifiée. Ce qui circule ici, c'est ce qu'il faut
 * pour l'afficher ou non, et dire depuis quand elle est là.
 */
export const schemaPhotoPersonne = z.object({
    poseeLe: z.iso.datetime(),
    octets: z.number().int().nonnegative(),
    /** `image/jpeg` ou `image/png` — lu dans les octets, jamais annoncé par le client. */
    type: z.string(),
});
/**
 * Plafond du dépôt. L'écran réduit l'image avant de l'envoyer (voir
 * `PHOTO_COTE_MAX`) ; ce plafond n'est donc pas la taille attendue mais la
 * limite au-delà de laquelle on refuse — un client peut toujours envoyer
 * autre chose que ce que l'écran prépare.
 */
export const TAILLE_MAX_PHOTO_OCTETS = 5 * 1024 * 1024;
/**
 * Le plus grand côté, en pixels, auquel l'écran réduit la photo avant de
 * l'envoyer. Une photo d'identité sert à reconnaître un visage au guichet,
 * pas à imprimer une affiche : au-delà, on stocke et on retransmet des
 * mégaoctets pour rien, sur des connexions qui ne les ont pas.
 */
export const PHOTO_COTE_MAX = 800;
/**
 * La photo ou le scan de la pièce qui prouve le lien d'un ayant droit
 * (décision du 28/09). Comme la photo d'identité : le fichier ne voyage
 * jamais dans la fiche, il se demande à part.
 */
export const schemaFichierPiece = z.object({
    poseLe: z.iso.datetime(),
    octets: z.number().int().nonnegative(),
    /** `application/pdf`, `image/jpeg`, `image/png` ou `image/webp` — lu dans les octets. */
    type: z.string(),
});
/**
 * Réunion du 15/10 — une pièce de la liste : un ayant droit peut en avoir
 * plusieurs, ajoutées à tout moment, jamais remplacées.
 */
export const schemaPieceListee = schemaFichierPiece.extend({
    id: z.uuid(),
    /** Ce que la pièce prouve, tel que l'agent l'a noté. */
    nature: z.enum(TYPES_JUSTIFICATIF),
    /** S1429 — ce qu'est une pièce « Autre » ; `null` pour les autres natures. */
    precision: z.string().nullable(),
});
/**
 * Plafond du dépôt d'une pièce. Plus large que la photo d'identité : un acte
 * doit rester LISIBLE, et un scan PDF de deux pages dépasse vite 5 Mo.
 * L'écran réduit les photos à `PIECE_COTE_MAX` avant l'envoi ; un PDF part
 * tel quel.
 */
export const TAILLE_MAX_PIECE_OCTETS = 10 * 1024 * 1024;
export const PIECE_COTE_MAX = 2000;
/** Un autre ayant droit du même lien, rattaché au même salarié. */
const schemaAyantDroitDuMemeLien = z.object({
    id: z.uuid(),
    nom: z.string(),
    prenom: z.string().nullable(),
    statut: z.enum(STATUTS_PERSONNE),
    etatConfirmation: z.enum(ETATS_CONFIRMATION),
    /** Compte-t-il face au DNS ? Ni un inactif ni un infirmé ne comptent. */
    compte: z.boolean(),
    /** Retiré par la chargée de clientèle (décision du 29/09), et pas désactivé pour une autre raison. */
    retire: z.boolean(),
});
/**
 * Le bloc « Contrôle » de la fiche d'un conjoint ou d'un enfant : ce qu'il
 * faut avoir sous les yeux pour le confirmer, sur pièce au guichet ou par la
 * chargée de clientèle. `null` pour les autres fiches, et pour qui ne
 * confirme rien — une pièce d'état civil n'a pas à s'afficher au médecin.
 */
export const schemaControleAyantDroit = z.object({
    lien: z.enum(LIENS_AYANT_DROIT),
    /** La dernière pièce posée. */
    fichier: schemaFichierPiece.nullable(),
    /** Réunion du 15/10 — toutes les pièces, la plus récente d'abord. */
    pieces: z.array(schemaPieceListee),
    /** Ce que le salarié a déclaré au DNS (colonnes C et E). */
    declares: z.object({ conjoints: z.number().int(), enfants: z.number().int() }),
    /** Les autres ayants droit du même lien, la fiche examinée exclue. */
    memeLien: z.array(schemaAyantDroitDuMemeLien),
    depasseLeDns: z.boolean(),
    /** La fiche a été confirmée sur pièce, à l'accueil — et non par la chargée. */
    confirmeSurPiece: z.boolean(),
    /** Le verdict du bouton « Confirmer sur pièce », avec la raison quand il est grisé. */
    surPiece: z.discriminatedUnion('possible', [
        z.object({ possible: z.literal(true) }),
        z.object({
            possible: z.literal(false),
            motif: z.enum(MOTIFS_REFUS_SUR_PIECE),
            message: z.string(),
        }),
    ]),
});
/**
 * Un contrat tel que le formulaire d'enrôlement le propose : c'est lui que
 * l'agent choisit, pas l'entreprise — une entreprise à deux offres a deux
 * contrats, et le salarié se rattache à l'un des deux (CP-08).
 */
export const schemaContratPourEnrolement = z.object({
    id: z.uuid(),
    offre: schemaOffreResume,
    /** « cadres », « non-cadres »… `null` quand le contrat couvre tout l'effectif. */
    populationLibelle: z.string().nullable(),
});
export const schemaFichePersonne = schemaPersonneTrouvee.extend({
    /**
     * Le métier du patient. ABSENT pour l'accueil (décision 279) : l'API ne
     * l'envoie pas, et l'écran ne montre ni ne demande ce qu'il n'a pas reçu.
     */
    fonction: z.string().nullable().optional(),
    lieuAffectation: z.string().nullable(),
    /** `AAAA-MM-JJ` — date civile. */
    dateEntree: z.iso.date().nullable(),
    /** D'où vient la fiche : la colonne « Origine » de la maquette B. */
    origine: z.enum(ORIGINES_DONNEE),
    /** Compteurs C et E du DNS — ils ne portent aucune identité (CP-01). */
    nbConjointDeclare: z.number().int(),
    nbEnfantDeclare: z.number().int(),
    justificatifLienParente: z.enum(TYPES_JUSTIFICATIF).nullable(),
    /** S1429 — ce qu'est la pièce quand sa nature est « Autre ». */
    justificatifPrecision: z.string().nullable(),
    lienParenteVerifieLe: z.iso.datetime().nullable(),
    /** `null` pour un client externe, ou pour un rôle sans `eligibilite:check`. */
    conditions: schemaConditionsEligibilite.nullable(),
    /** Vide pour un ayant droit ou un externe : seul un salarié en a. */
    ayantsDroit: z.array(schemaPersonneTrouvee),
    /**
     * RG-PA-02 — la visite se paie au comptoir : le client externe, ou le
     * salarié non couvert. `tarif` en ariary, lu dans la grille de la caisse
     * (ligne « généraliste, externe ») ; `null` quand la grille n'a pas cette
     * ligne. `aPartirDe` : plusieurs lignes actives, `tarif` est la moins chère
     * et le tarif exact se choisit à la caisse (décision 178). `null` en entier
     * quand la visite est prise en charge, ou que le rôle ne lit pas le verdict.
     */
    paiementImmediat: z
        .object({ tarif: z.number().int().nullable(), aPartirDe: z.boolean() })
        .nullable(),
    /** `null` quand aucune photo n'a été déposée — le cas de la très grande majorité des fiches. */
    photo: schemaPhotoPersonne.nullable(),
    /** Voir `schemaControleAyantDroit` : `null` hors d'un ayant droit, ou pour qui ne confirme pas. */
    controle: schemaControleAyantDroit.nullable(),
    /**
     * Le conjoint ou l'enfant a été retiré par la chargée de clientèle
     * (décision du 29/09) : pourquoi, et depuis quand. `null` sinon — y compris
     * pour une fiche désactivée pour une autre raison.
     */
    retrait: z
        .object({
        motif: z.enum(MOTIFS_RETRAIT_AYANT_DROIT),
        depuis: z.iso.datetime(),
    })
        .nullable(),
    /**
     * Le salarié a été déclaré parti par la chargée de clientèle (réunion du
     * 15/10) : pourquoi, à quelle date, et quand ce fut saisi. `null` sinon —
     * y compris pour un départ lu dans un DNS.
     */
    depart: z
        .object({
        motif: z.enum(MOTIFS_DEPART_SALARIE),
        date: z.iso.date().nullable(),
        depuis: z.iso.datetime(),
    })
        .nullable(),
    /**
     * Décision 355 — la chargée choisit le contrat sur la fiche, comme dans la
     * liste « À confirmer » : le contrat retenu à l'accueil, et les contrats
     * actifs de l'entreprise. Envoyés seulement par la lecture de la fiche,
     * pour un salarié et à qui confirme : confirmé, il change de contrat dans
     * son entreprise (décision 374) ; absents sinon.
     */
    contratId: z.uuid().nullable().optional(),
    contratsEntreprise: z.array(schemaContratPourEnrolement).optional(),
});
/**
 * Retirer un ayant droit déjà confirmé (`ayant_droit:retirer`, décision du
 * 29/09). Le motif est obligatoire, choisi dans une courte liste : jamais un
 * texte libre, qui porterait tôt ou tard un nom jusque dans le journal.
 */
export const schemaRetraitAyantDroit = z.object({
    motif: z.enum(MOTIFS_RETRAIT_AYANT_DROIT, {
        error: 'Choisissez le motif du retrait.',
    }),
});
/** Réunion du 15/10 — le départ d'un salarié, déclaré à la main. */
export const schemaDepartSalarie = z.object({
    motif: z.enum(MOTIFS_DEPART_SALARIE, {
        error: 'Choisissez le motif du départ.',
    }),
    dateDepart: z.iso.date('La date de départ est obligatoire.'),
});
// ─────────────────────────────────────────────────────────────────────
//  File « à confirmer » — la migration au fil de l'eau (CP-13)
// ─────────────────────────────────────────────────────────────────────
/**
 * La file que la chargée de clientèle vide chaque jour contre son classeur :
 * les fiches créées au guichet, servies d'abord, confirmées ensuite. Par
 * défaut dans l'ordre des visites — la plus ancienne en tête.
 */
export const TRIS_A_CONFIRMER = ['enrolement', 'nom', 'matricule', 'entreprise', 'lien'];
/**
 * Les deux vues de la file : ce qui attend une décision, et ce qui a été
 * infirmé — pour revenir sur une infirmation faite à tort.
 */
export const ETATS_FILE_CONFIRMATION = ['A_CONFIRMER', 'INFIRME'];
export const schemaFiltresAConfirmer = z.object({
    etat: z.enum(ETATS_FILE_CONFIRMATION).default('A_CONFIRMER'),
    /**
     * Une entreprise à la fois : la chargée de clientèle confirme contre le
     * DNS papier d'UNE entreprise, qu'elle a sous la main.
     */
    adherentId: z.uuid().optional(),
    page: z.coerce.number().int().min(1).default(1),
    limite: z.coerce.number().int().min(1).max(100).default(25),
    tri: z.enum(TRIS_A_CONFIRMER).default('enrolement'),
    ordre: z.enum(ORDRES_DE_TRI).default('asc'),
});
export const schemaPersonneAConfirmer = schemaPersonneTrouvee.extend({
    /** Instant de l'enrôlement au guichet. */
    enroleLe: z.iso.datetime(),
    /** Renseigné quand la fiche a été créée pendant une coupure, puis synchronisée. */
    creeHorsLigneLe: z.iso.datetime().nullable(),
    /**
     * Un ayant droit de plus que ce que son salarié a déclaré au DNS : c'est à
     * la chargée de trancher — garder les deux, ou retirer l'ancien.
     */
    depasseLeDns: z.boolean(),
    /** Le contrat du salarié, choisi à l'accueil. `null` pour un ayant droit. */
    contratId: z.uuid().nullable(),
    /**
     * Décision 338 (4) — les contrats actifs de l'entreprise d'un salarié : à
     * plus d'un, la chargée choisit le sien en confirmant. Vide pour un ayant
     * droit, qui suit son salarié.
     */
    contratsEntreprise: z.array(schemaContratPourEnrolement),
    /** Décision 343 — la note de l'accueil, à lire avant de confirmer. */
    noteChargee: schemaNoteChargee.nullable(),
    /** U1 (décision 490) — la chargée qui traite cette fiche, et depuis quand (R2). */
    priseEnCharge: schemaPriseEnCharge.nullable().optional(),
});
export const schemaPageAConfirmer = z.object({
    elements: z.array(schemaPersonneAConfirmer),
    page: z.number().int(),
    limite: z.number().int(),
    total: z.number().int(),
});
/**
 * Ce que la chargée de clientèle décide, fiche par fiche.
 *
 * Pas de motif à saisir : il se déduit de la personne. Un salarié infirmé
 * est absent du DNS de son entreprise ; un ayant droit infirmé n'est pas
 * reconnu par son salarié. Un texte libre ici finirait par porter des
 * éléments nominatifs jusque dans le journal.
 */
export const DECISIONS_CONFIRMATION = ['CONFIRME', 'INFIRME'];
export const schemaDecisionConfirmation = z.object({
    decision: z.enum(DECISIONS_CONFIRMATION),
    /**
     * Décision 338 (4) — le contrat du salarié, obligatoire quand son
     * entreprise en a plusieurs d'actifs (`contratALaConfirmation`).
     */
    contratId: z.uuid().optional(),
});
/**
 * Revenir sur une infirmation confirme de nouveau la fiche : même règle de
 * contrat que la confirmation (décision 338, point 4). Corps facultatif
 * (Fastify le lit `null` quand il manque) : sans lui, le contrat est pris
 * d'office s'il n'y en a qu'un.
 */
export const schemaAnnulationInfirmation = z.object({ contratId: z.uuid().optional() }).nullish();
// ─────────────────────────────────────────────────────────────────────
//  Rattacher un client externe à son entreprise
// ─────────────────────────────────────────────────────────────────────
/**
 * Le client externe revient avec la carte de son entreprise : il devient
 * salarié de ce contrat, sur la même fiche — ses visites passées restent à
 * lui, et aucun doublon n'est créé. La fiche repasse « à confirmer ».
 */
export const schemaRattachementEntreprise = z.object({
    contratId: z.uuid(),
    /** Tel quel, zéros de tête compris (CP-10). */
    matricule: z.string().trim().max(60).nullish(),
});
/** Décision 459 — le refus d'une date d'entrée future, au serveur comme à l'écran. */
export const DATE_ENTREE_FUTURE = 'La date d’entrée ne peut pas être dans le futur.';
/**
 * Compléter la fiche administrative (`patient:update`, recette du 28/09).
 *
 * Chaque champ est facultatif : absent, il ne change pas ; `null`, il
 * s'efface. Pas le nom, le prénom ni le matricule : c'est par eux que le DNS
 * retrouve la personne, et une retouche au guichet ferait naître un doublon
 * au prochain import. La qualité d'identité se recalcule côté serveur.
 */
export const schemaModificationPersonne = z
    .object({
    /**
     * Réunion du 15/10 : l'accueil et la chargée corrigent aussi le nom et
     * le prénom. Le journal garde l'ancienne et la nouvelle identité ; le DNS
     * retrouve la personne sous l'écriture d'avant (`nomCompletDns`) comme
     * sous la nouvelle.
     */
    nom: z.string().trim().min(1, 'Le nom ne peut pas être vide.').max(120).optional(),
    prenom: z.string().trim().max(120).nullable().optional(),
    dateNaissance: z.iso
        .date()
        .refine((d) => d <= dateCivileAMadagascar(), {
        message: 'La date de naissance ne peut pas être dans le futur.',
    })
        .nullable()
        .optional(),
    sexe: z.enum(SEXES).nullable().optional(),
    /** Les trois suivants ne valent que pour un salarié. */
    fonction: z.string().trim().max(120).nullable().optional(),
    lieuAffectation: z.string().trim().max(120).nullable().optional(),
    // Décision 459 — jamais dans le futur, comme à la création.
    dateEntree: z.iso
        .date()
        .refine((d) => d <= dateCivileAMadagascar(), { message: DATE_ENTREE_FUTURE })
        .nullable()
        .optional(),
})
    .refine((m) => Object.values(m).some((v) => v !== undefined), {
    message: 'Rien à modifier.',
});
// ─────────────────────────────────────────────────────────────────────
//  Entreprises pour l'enrôlement — le second champ du formulaire PA-04
// ─────────────────────────────────────────────────────────────────────
/**
 * L'agent tape un fragment du nom, du code ou d'un alias (CP-06) et choisit
 * l'entreprise dans la liste courte qui en résulte. Pas de pagination : au
 * guichet, dix propositions suffisent — au-delà, c'est le terme qui est
 * trop court, pas la liste.
 */
export const schemaRechercheAdherents = z.object({
    recherche: z.string().trim().min(2, 'Deux caractères au minimum.').max(120),
    limite: z.coerce.number().int().min(1).max(50).default(10),
});
/**
 * L'entreprise avec ses contrats actifs, pour un `<select>` ou une liste
 * de propositions. Le statut est joint pour que l'agent voie AVANT de
 * créer la fiche qu'il enrôle chez une entreprise suspendue — la fiche se
 * crée quand même (le référentiel n'est pas le verdict), mais il le sait.
 *
 * Une entreprise sans contrat actif arrive avec `contrats: []` : elle
 * existe, on ne peut rien y rattacher, et l'écran doit le dire plutôt que
 * de la faire disparaître de la liste.
 */
export const schemaAdherentPourEnrolement = schemaAdherentResume.extend({
    contrats: z.array(schemaContratPourEnrolement),
});
/** Revue S1027, AC2 — le même message que la modification d'une fiche. */
export const DATE_NAISSANCE_FUTURE = 'La date de naissance ne peut pas être dans le futur.';
/**
 * Enrôlement au guichet — CCT_01 § 2.3, PA-04 « ajouter un nouveau patient
 * (première visite) ».
 *
 * **Le formulaire le plus court possible, et c'est un choix de conception.**
 * PRISME reçoit environ deux cents passages par jour ; chaque champ ajouté
 * ici est payé par la file d'attente. Date de naissance, sexe, fonction,
 * lieu d'affectation, date d'entrée : rien de tout cela n'est exigé. Ces
 * valeurs arrivent par l'import DNS ou par la chargée de clientèle, hors du
 * chemin critique (CP-13, migration au fil de l'eau).
 *
 * Trois champs tapés dans les deux cas courants :
 *
 *   salarié inconnu       entreprise (liste), nom, prénom, matricule si connu
 *   ayant droit           titulaire (recherche), nom, prénom, type
 *                         (+ date de naissance pour un enfant)
 *
 * Décisions 404 et 407 : pour un enfant, la date de naissance est
 * obligatoire — l'accueil la complète, elle n'est plus une raison d'envoyer
 * à la chargée. Pour un conjoint, elle reste facultative.
 *
 * Ce que le serveur remplit SEUL, et qui n'est donc pas dans ce schéma :
 * l'origine de la donnée, la qualité d'identité (mesurée, jamais déclarée
 * — voir `qualiteIdentiteDe`), le nom complet normalisé, l'état de
 * confirmation et l'horodatage de la vérification du justificatif.
 */
const traitsSaisis = {
    nom: z.string().trim().min(1, 'Le nom est obligatoire.').max(120),
    prenom: z.string().trim().max(120).nullish(),
    /**
     * `AAAA-MM-JJ`. Facultative au guichet pour un salarié ou un client
     * externe : l'exiger bloquerait la file pour une donnée que l'import
     * apportera. Elle fait monter la qualité d'identité quand elle est là.
     * Obligatoire pour un enfant (décisions 404 et 407).
     */
    // Décision 409 — jamais dans le futur, quel que soit le lien (conjoint
    // compris), comme à la modification d'une fiche. Jour civil de Madagascar.
    dateNaissance: z.iso
        .date()
        .refine((d) => d <= dateCivileAMadagascar(), { message: DATE_NAISSANCE_FUTURE })
        .nullish(),
    sexe: z.enum(SEXES).nullish(),
    /**
     * Mode dégradé (CP-12) — la fiche a été remplie sur le formulaire papier
     * pendant une coupure, à cette date : on la ressaisit au retour du courant.
     * Elle arrive dans « À confirmer » marquée « Pendant une coupure ».
     */
    creeHorsLigneLe: z.iso.datetime().nullish(),
};
/**
 * Un conjoint ou un enfant se rattache à un titulaire, et à lui seul : son
 * entreprise, son contrat et son offre en découlent (RG-PA-03). Il n'a donc
 * PAS de `contratId` à saisir — en donner un ouvrirait la porte à un ayant
 * droit rattaché à une entreprise autre que celle de son salarié.

 */
const traitsAyantDroit = {
    ...traitsSaisis,
    titulaireId: z.uuid(),
    /**
     * Le justificatif du lien de parenté, quand il est présenté (CP-02).
     * **Facultatif, et délibérément** : l'exiger refuserait les ayants droit
     * reconstitués depuis le registre d'affluences (CP-01), qui viennent
     * depuis des années sans avoir jamais rien présenté.
     */
    justificatifLienParente: z.enum(TYPES_JUSTIFICATIF).nullish(),
    /** S1429 — obligatoire avec « Autre » (le serveur le vérifie), ignorée sinon. */
    justificatifPrecision: z.string().trim().max(PRECISION_PIECE_MAX).nullish(),
    /** Décision 343 — une note pour la chargée, qui confirmera la fiche. */
    noteChargee: champNoteChargeeALEnrolement,
};
/** Décision 407 — le refus d'un enfant sans date, au serveur comme à l'écran. */
export const DATE_NAISSANCE_ENFANT_OBLIGATOIRE = 'La date de naissance est obligatoire pour un enfant.';
export const schemaEnrolementPersonne = z.discriminatedUnion('typeBeneficiaire', [
    z.object({
        typeBeneficiaire: z.literal('TITULAIRE'),
        ...traitsSaisis,
        /** L'entreprise, choisie dans une liste. C'est elle qui porte le statut. */
        contratId: z.uuid(),
        /** Tel quel, zéros de tête compris. Absent dans 20 % des cas (CP-10). */
        matricule: z.string().trim().max(60).nullish(),
        noteChargee: champNoteChargeeALEnrolement,
        /**
         * Décision 459 — facultative et purement informative : elle ne bloque
         * rien et ne sert à aucune règle d'éligibilité. Jamais dans le futur
         * (décision 409), jour civil de Madagascar. Titulaire seulement.
         */
        dateEntree: z.iso
            .date()
            .refine((d) => d <= dateCivileAMadagascar(), { message: DATE_ENTREE_FUTURE })
            .nullish(),
    }),
    z.object({ typeBeneficiaire: z.literal('CONJOINT'), ...traitsAyantDroit }),
    z.object({
        typeBeneficiaire: z.literal('ENFANT'),
        ...traitsAyantDroit,
        // Décision 407 — obligatoire pour un enfant : son âge compte.
        // Elle décide seule de l'âge : une date future le ferait passer sous la
        // limite (revue S1027, AC2). Jour civil de Madagascar.
        dateNaissance: z.iso
            .date({
            error: (probleme) => probleme.input == null
                ? DATE_NAISSANCE_ENFANT_OBLIGATOIRE
                : 'Date invalide (AAAA-MM-JJ).',
        })
            .refine((d) => d <= dateCivileAMadagascar(), { message: DATE_NAISSANCE_FUTURE }),
    }),
    /** RG-PA-02 — ni entreprise ni titulaire : il paie, il est soigné. */
    z.object({ typeBeneficiaire: z.literal('EXTERNE'), ...traitsSaisis }),
]);
/**
 * Décisions 360 et 404 — la fiche créée au guichet, et, pour un ayant droit
 * (conjoint ou enfant) examiné pour l'entrée directe, le verdict : admis, ou
 * ce qui manque. `null` quand la règle ne s'applique pas (salarié, client
 * externe, interrupteur éteint, fiche confirmée par la chargée elle-même).
 */
export const schemaPersonneEnrolee = schemaPersonneTrouvee.extend({
    ayantDroitSansConfirmation: z
        .object({
        admis: z.boolean(),
        manque: z.array(z.enum(CONDITIONS_AYANT_DROIT_SANS_CONFIRMATION)),
        ageLimiteEnfant: z.number().int(),
    })
        .nullable(),
});
//# sourceMappingURL=personnes.js.map